<template>
  <div id="app" :class="{ cypress: isCypress, wholesale: !isTelekom }">
    <VHeader />
    <component :is="LayoutComponent" :key="LayoutComponent.name || LayoutComponent.__file" v-slot="slotProps">
      <transition name="fade" mode="out-in" appear @after-enter="animationComplete">
        <RouterView :key="key" :is-navigation-flat="slotProps.isNavigationFlat"></RouterView>
      </transition>
    </component>
    <TimeoutWindow />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import VHeader from '@/components/UI/Header';
import LoadingLayout from '@/layouts/Loading';
import DefaultLayout from '@/layouts/Default';
import emitter from '@/plugins/emitter';
import { ERROR } from '@route-types';
import TimeoutWindow from '@/components/TimeoutWindow.vue';

export default {
  name: 'TrinitySpace',

  components: {
    TimeoutWindow,
    VHeader,
  },

  metaInfo() {
    return {
      title: '',
      titleTemplate: (titleChunk) => {
        // If undefined or blank then we don't need the separator
        const suffix = this.$t('common.pageTitle');
        return titleChunk ? `${titleChunk} | ${suffix}` : suffix;
      },

      htmlAttrs: {
        lang: this.$i18n.locale,
      },
    };
  },

  computed: {
    ...mapState('system', ['initComplete', 'initPromise', 'isFirstCall', 'entranceType', 'sessionId']),
    ...mapGetters('system', ['isTelekom', 'isAuthenticated']),

    key() {
      if (this.$route.matched.some((item) => item.meta.refreshOnLanguageChange)) {
        return this.$i18n.locale;
      }
      return 'app-view';
    },

    LayoutComponent() {
      const layout =
        (this.$route.meta && this.$route.meta.layout) ||
        (this.$route.matched.length > 0 && this.$route.matched[0].meta && this.$route.matched[0].meta.layout) ||
        DefaultLayout;

      if (this.$route.matched.length && this.$route.matched[0].name === ERROR) return layout;
      if (!this.initComplete || this.$route.name === null) return LoadingLayout;
      return layout;
    },

    isCypress() {
      return !!window.Cypress;
    },
  },

  watch: {
    isAuthenticated(newValue) {
      if (newValue) {
        this.initializeTimer();
      } else {
        this.cleanupTimer();
      }
    },
  },

  async mounted() {
    document.previousElement = document.getElementById('app'); // ensure that previousElement is not null

    await this.initPromise;
    if (this.isFirstCall) {
      if (['AUTH_RETURN_ITS', 'AUTH_RETRY_ITS'].includes(this.entranceType)) {
        this.$webtrekk.viewPage('authsite.its', 'authentication.common');
      } else if (this.entranceType === 'AUTH_OPENID_RETURN') {
        this.$webtrekk.viewPage('authsite.openid', 'authentication.common');
      }
    }

    window.addEventListener('storage', ({ storageArea, key, newValue }) => {
      if (storageArea !== sessionStorage) return;
      if (newValue === null) return;

      switch (key) {
        case 'languageEvent': {
          const [locale, sessionId] = newValue.split(';');
          if (this.sessionId !== sessionId) return;
          this.$i18n.locale = locale;
          break;
        }
        case 'logoutEvent':
          if (!this.isAuthenticated || this.sessionId !== newValue) return;
          this.$store.dispatch('system/logoutEvent');
          break;
      }
    });
  },

  methods: {
    ...mapActions('timer', ['initializeTimer', 'cleanupTimer']),

    animationComplete() {
      emitter.emit('animationComplete');
    },
  },
};
</script>

<!-- This should generally be the only global CSS in the app. -->
<style lang="scss">
// Allow element/type selectors, because this is global CSS.
// stylelint-disable selector-max-type, selector-class-pattern

// Normalize default styles across browsers,
// https://necolas.github.io/normalize.css/
@import '~normalize.css/normalize.css';

// Telekom Brand Design
@import '@branddesign';

// Our own design
@import '@design';

.wholesale {
  @import '@wholesale';
}
// WholesalePartner Design
</style>
